import {
  BehaviorSubject,
  Observable,
  combineLatest,
  distinctUntilChanged,
  map,
} from 'rxjs';
import { DetailedCustomer } from '../../shared/models/customer.model';
import { DepartmentWithResidents } from '../../shared/models/department.model';
import { Resident } from '../../shared/models/resident.model';

export interface ShopSelection {
  customer: DetailedCustomer | null;
  department: DepartmentWithResidents | null;
  resident: Resident | null;
}

const DEPARTMENT_TYPE_ORDER: Record<string, number> = { B2C: 0, B2B: 1 };

export abstract class ShopSelectionStore {
  private readonly customerSubject =
    new BehaviorSubject<DetailedCustomer | null>(null);
  private readonly departmentSubject =
    new BehaviorSubject<DepartmentWithResidents | null>(null);
  private readonly residentIdSubject = new BehaviorSubject<number | null>(null);

  readonly customer$: Observable<DetailedCustomer | null> =
    this.customerSubject.asObservable();
  readonly department$: Observable<DepartmentWithResidents | null> =
    this.departmentSubject.asObservable();
  readonly resident$: Observable<Resident | null> = combineLatest([
    this.departmentSubject,
    this.residentIdSubject,
  ]).pipe(
    map(([department, residentId]) =>
      this.findResident(department, residentId)
    ),
    distinctUntilChanged()
  );
  readonly isFirstLogin$: Observable<boolean> = this.customer$.pipe(
    map(customer => !!customer && customer.departments.length === 0),
    distinctUntilChanged()
  );

  get snapshot(): ShopSelection {
    const department = this.departmentSubject.value;
    return {
      customer: this.customerSubject.value,
      department,
      resident: this.findResident(department, this.residentIdSubject.value),
    };
  }

  protected setCustomer(customer: DetailedCustomer | null): void {
    if (customer) sortDepartments(customer);
    this.customerSubject.next(customer);
  }

  protected setDepartment(department: DepartmentWithResidents | null): void {
    this.departmentSubject.next(department);
  }

  protected setResidentId(residentId: number | null): void {
    if (residentId === this.residentIdSubject.value) return;
    this.residentIdSubject.next(residentId);
  }

  updateCustomer(customer: DetailedCustomer): void {
    if (customer.id !== this.customerSubject.value?.id) return;
    this.setCustomer(customer);
  }

  updateDepartment(department: DepartmentWithResidents): void {
    const selected = this.departmentSubject.value;
    if (department.departmentId !== selected?.departmentId) return;
    this.setDepartment(department);
  }

  patchResident(residentId: number, changes: Partial<Resident>): void {
    const department = this.departmentSubject.value;
    if (!department) return;
    this.setDepartment(
      new DepartmentWithResidents({
        ...department,
        people: department.people.map(person =>
          person.id === residentId
            ? new Resident({ ...person, ...changes })
            : person
        ),
      })
    );
  }

  clear(): void {
    this.setCustomer(null);
    this.setDepartment(null);
    this.setResidentId(null);
  }

  private findResident(
    department: DepartmentWithResidents | null,
    residentId: number | null
  ): Resident | null {
    return department?.people.find(p => p.id === residentId) ?? null;
  }
}

function sortDepartments(customer: DetailedCustomer): void {
  customer.departments = customer.departments.sort(
    (a, b) =>
      DEPARTMENT_TYPE_ORDER[a.departmentType] -
        DEPARTMENT_TYPE_ORDER[b.departmentType] || a.name.localeCompare(b.name)
  );
}
